import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

const EMPTY = {
  code: "", flag: "", name_en: "", name_ar: "", region: "",
  varieties: [], altitude: "", harvest: "", process: [],
  flavor_en: "", flavor_ar: "", description_en: "", description_ar: "",
};

export default function AdminCountriesTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { data: countries = [], isLoading } = useQuery({ queryKey: ["countries"], queryFn: endpoints.countries });
  const [editing, setEditing] = useState(null); // null | "new" | country object

  const onSave = async (form) => {
    try {
      const payload = {
        ...form,
        varieties: typeof form.varieties === "string" ? form.varieties.split(",").map((s) => s.trim()).filter(Boolean) : form.varieties,
        process: typeof form.process === "string" ? form.process.split(",").map((s) => s.trim()).filter(Boolean) : form.process,
      };
      if (editing === "new" || !editing?.code || !countries.find((c) => c.code === editing.code)) {
        await endpoints.adminCreateCountry(payload);
        toast.success(isRTL ? "تمت الإضافة" : "Country added");
      } else {
        await endpoints.adminUpdateCountry(editing.code, payload);
        toast.success(isRTL ? "تم الحفظ" : "Saved");
      }
      qc.invalidateQueries({ queryKey: ["countries"] });
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الحفظ" : "Save failed"));
    }
  };

  const onDelete = async (code) => {
    if (!window.confirm(isRTL ? "حذف هذه الدولة؟" : "Delete this country?")) return;
    try {
      await endpoints.adminDeleteCountry(code);
      toast.success(isRTL ? "تم الحذف" : "Deleted");
      qc.invalidateQueries({ queryKey: ["countries"] });
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-countries-tab">
      <div className="flex justify-between items-center">
        <div className="text-sm text-[#5C544F]">
          {isRTL ? `${countries.length} دولة` : `${countries.length} countries`}
        </div>
        <Button
          onClick={() => setEditing("new")}
          className="rounded-full bg-[#C86240] hover:bg-[#b15637] text-white"
          data-testid="admin-add-country-btn"
        >
          <Plus className="w-4 h-4 me-2" /> {isRTL ? "دولة جديدة" : "New country"}
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="text-[#5C544F]">…</div>
        ) : (
          countries.map((c) => (
            <div key={c.code} className="rounded-2xl border border-[#2A2421]/10 bg-white p-4" data-testid={`admin-country-card-${c.code}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{c.flag}</span>
                  <span className="font-semibold text-[#2A2421]">{c.name_en}</span>
                  <span className="text-xs text-[#5C544F]">{c.code}</span>
                </div>
              </div>
              <div className="text-xs text-[#5C544F] mb-3">{c.region}</div>
              <div className="text-xs text-[#5C544F] line-clamp-2">{c.description_en}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing(c)} data-testid={`admin-edit-country-${c.code}`}>
                  <Pencil className="w-3.5 h-3.5 me-1" /> {isRTL ? "تعديل" : "Edit"}
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => onDelete(c.code)} data-testid={`admin-delete-country-${c.code}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <CountryFormDialog
        key={editing === "new" ? "new" : (editing?.code || "closed")}
        open={!!editing}
        initial={editing === "new" ? EMPTY : editing}
        onClose={() => setEditing(null)}
        onSave={onSave}
        isNew={editing === "new"}
      />
    </div>
  );
}

function CountryFormDialog({ open, initial, onClose, onSave, isNew }) {
  const { isRTL } = useI18n();
  const [form, setForm] = useState(initial || EMPTY);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-[#F9F6F0]" data-testid="admin-country-form">
        <DialogHeader>
          <DialogTitle className="text-start">
            {isNew ? (isRTL ? "إضافة دولة" : "Add country") : (isRTL ? "تعديل دولة" : "Edit country")}
          </DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pe-2">
          <FormField label={isRTL ? "كود (ISO)" : "Code (ISO)"}>
            <Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} disabled={!isNew} maxLength={3} data-testid="country-code-input" />
          </FormField>
          <FormField label={isRTL ? "العَلَم (إيموجي)" : "Flag emoji"}>
            <Input value={form.flag} onChange={(e) => set("flag", e.target.value)} data-testid="country-flag-input" />
          </FormField>
          <FormField label={isRTL ? "الاسم بالإنجليزي" : "Name (EN)"}>
            <Input value={form.name_en} onChange={(e) => set("name_en", e.target.value)} data-testid="country-name-en-input" />
          </FormField>
          <FormField label={isRTL ? "الاسم بالعربي" : "Name (AR)"}>
            <Input value={form.name_ar} onChange={(e) => set("name_ar", e.target.value)} dir="rtl" data-testid="country-name-ar-input" />
          </FormField>
          <FormField label={isRTL ? "المنطقة" : "Region"}>
            <Input value={form.region} onChange={(e) => set("region", e.target.value)} />
          </FormField>
          <FormField label={isRTL ? "الارتفاع" : "Altitude"}>
            <Input value={form.altitude} onChange={(e) => set("altitude", e.target.value)} />
          </FormField>
          <FormField label={isRTL ? "موسم الحصاد" : "Harvest"}>
            <Input value={form.harvest} onChange={(e) => set("harvest", e.target.value)} />
          </FormField>
          <FormField label={isRTL ? "الأصناف (مفصولة بفاصلة)" : "Varieties (comma-sep)"}>
            <Input value={Array.isArray(form.varieties) ? form.varieties.join(", ") : form.varieties} onChange={(e) => set("varieties", e.target.value)} />
          </FormField>
          <FormField label={isRTL ? "المعالجة (مفصولة بفاصلة)" : "Process (comma-sep)"}>
            <Input value={Array.isArray(form.process) ? form.process.join(", ") : form.process} onChange={(e) => set("process", e.target.value)} />
          </FormField>
          <FormField label={isRTL ? "النكهة (إنجليزي)" : "Flavor (EN)"} colSpan>
            <Textarea rows={2} value={form.flavor_en} onChange={(e) => set("flavor_en", e.target.value)} />
          </FormField>
          <FormField label={isRTL ? "النكهة (عربي)" : "Flavor (AR)"} colSpan>
            <Textarea rows={2} value={form.flavor_ar} onChange={(e) => set("flavor_ar", e.target.value)} dir="rtl" />
          </FormField>
          <FormField label={isRTL ? "الوصف (إنجليزي)" : "Description (EN)"} colSpan>
            <Textarea rows={3} value={form.description_en} onChange={(e) => set("description_en", e.target.value)} />
          </FormField>
          <FormField label={isRTL ? "الوصف (عربي)" : "Description (AR)"} colSpan>
            <Textarea rows={3} value={form.description_ar} onChange={(e) => set("description_ar", e.target.value)} dir="rtl" />
          </FormField>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#2A2421]/10">
          <Button variant="ghost" onClick={onClose}>{isRTL ? "إلغاء" : "Cancel"}</Button>
          <Button
            onClick={() => onSave(form)}
            className="bg-[#2A2421] text-white"
            data-testid="admin-save-country-btn"
          >
            {isRTL ? "حفظ" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const FormField = ({ label, children, colSpan }) => (
  <label className={`block ${colSpan ? "sm:col-span-2" : ""}`}>
    <span className="block text-xs text-[#5C544F] mb-1">{label}</span>
    {children}
  </label>
);
